import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal, Button, ComboField, ComboFieldItem } from "@truevault/aquilo";
import { ArrowLeftIcon, MagnifyingGlassIcon, Squares2X2Icon } from "@heroicons/react/20/solid";
import { Combobox } from "@headlessui/react";
import { CircularProgress } from "@mui/material";
import clsx from "clsx";
import debounce from "lodash.debounce";
import { VendorDto } from "../../../../common/service/server/dto/VendorDto";
import {
  useDataRecipientsCatalog,
  useDataSourceCatalog,
} from "../../../../common/hooks/useVendors";
import { OrganizationPublicId } from "../../../../common/service/server/Types";
import { Api } from "../../../../common/service/Api";
import {
  CollectionContext,
  OrganizationDataSourceDto,
} from "../../../../common/service/server/dto/OrganizationDataRecipientDto";
import {
  SOURCE_KEY_CONSUMER_DIRECTLY,
  SOURCE_KEY_OTHER_INDIVIDUALS,
  VENDOR_CATEGORY_AD_NETWORK,
  VENDOR_CATEGORY_DATA_ANALYTICS_PROVIDER,
  VENDOR_CATEGORY_DATA_BROKER,
  VENDOR_CATEGORY_RETAIL_PARTNER,
} from "../../../types/Vendor";

export const OTHER_SOURCE_EXCLUDED_CATEGORIES = [
  VENDOR_CATEGORY_AD_NETWORK,
  VENDOR_CATEGORY_DATA_BROKER,
  VENDOR_CATEGORY_RETAIL_PARTNER,
  VENDOR_CATEGORY_DATA_ANALYTICS_PROVIDER,
] as const;

export const OTHER_SOURCE_EXCLUDED_VENDOR_KEYS = [
  SOURCE_KEY_CONSUMER_DIRECTLY,
  SOURCE_KEY_OTHER_INDIVIDUALS,
] as const;

const INITIAL_SUGGESTIONS_COUNT = 8;

const CUSTOM_SOURCE_OPTION = {
  id: "_CUSTOM_SOURCE_OPTION",
  name: "Add Custom Source",
  logoUrl: "",
  isSpecial: true,
};

type ModalView = "main" | "custom-source-details";

export function isOtherConsumerDataSource(source: OrganizationDataSourceDto): boolean {
  return isOtherDataSource(
    source,
    "CONSUMER",
    OTHER_SOURCE_EXCLUDED_CATEGORIES,
    OTHER_SOURCE_EXCLUDED_VENDOR_KEYS,
  );
}

export function isOtherDataSource(
  source: OrganizationDataSourceDto,
  collectionContext: CollectionContext,
  excludedCategories: readonly string[],
  excludedVendorKeys: readonly string[],
): boolean {
  if (!source.collectionContext.includes(collectionContext)) {
    return false;
  }
  if (source.vendorKey && excludedVendorKeys.includes(source.vendorKey)) {
    return false;
  }
  if (source.category && excludedCategories.includes(source.category)) {
    return false;
  }
  return true;
}

export type CustomSourceFields = {
  name: string;
  category: string;
  url?: string;
  contactEmail?: string;
};

export interface SelectedSource {
  id?: string;
  name: string;
  vendorKey?: string;
  logoUrl?: string;
  custom?: CustomSourceFields;
}

/** Passed from parent state when opening the add sources modal. */
export type LaunchAddSourcesModalConfig = {
  title?: string;
  description?: string;
  searchPlaceholder?: string;
};

export function AddSourceModal({
  organizationId,
  existingSourceIds,
  existingSourceNames,
  otherExcludedVendorKeys = OTHER_SOURCE_EXCLUDED_VENDOR_KEYS,
  title = "Add source",
  description = "Choose vendors from the catalog so we can attach them as data sources.",
  searchPlaceholder = "Search over 2,500 vendors or add a custom source",
  onClose,
  onDone,
}: {
  organizationId: OrganizationPublicId;
  existingSourceIds: Set<string>;
  existingSourceNames?: Set<string>;
  otherExcludedVendorKeys?: readonly string[];
  title?: string;
  description?: string;
  searchPlaceholder?: string;
  onClose: () => void;
  onDone: (selectedSources: SelectedSource[]) => void | Promise<void>;
}) {
  const [dataSourceCatalog] = useDataSourceCatalog(organizationId);
  const [dataRecipientCatalog] = useDataRecipientsCatalog(organizationId, false);

  const vendorCatalog = useMemo(() => {
    // Include both catalog roles, avoiding duplicates for vendors that have both roles.
    return Array.from(
      new Map(
        [...dataRecipientCatalog, ...dataSourceCatalog].map((vendor) => [vendor.id, vendor]),
      ).values(),
    );
  }, [dataSourceCatalog, dataRecipientCatalog]);

  const [selectedSources, setSelectedSources] = useState<SelectedSource[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentView, setCurrentView] = useState<ModalView>("main");
  const [isLoading, setIsLoading] = useState(false);

  const selectedSourceIds = useMemo(
    () => new Set(selectedSources.map((s) => s.id).filter((id): id is string => Boolean(id))),
    [selectedSources],
  );

  const selectedSourceNamesLower = useMemo(
    () => new Set(selectedSources.map((s) => s.name.trim().toLowerCase())),
    [selectedSources],
  );

  const categoryOptions = useMemo((): ComboFieldItem[] => {
    const categories = new Set<string>();
    for (const vendor of vendorCatalog) {
      if (otherExcludedVendorKeys.includes(vendor.vendorKey)) {
        continue;
      }
      if (vendor.category) {
        categories.add(vendor.category);
      }
    }
    return Array.from(categories)
      .sort((a, b) => a.localeCompare(b))
      .map((name) => ({ id: name, name }));
  }, [vendorCatalog, otherExcludedVendorKeys]);

  const eligibleVendors = useMemo(() => {
    return vendorCatalog.filter((vendor) => {
      if (existingSourceIds.has(vendor.id)) {
        return false;
      }
      if (selectedSourceIds.has(vendor.id)) {
        return false;
      }
      return !otherExcludedVendorKeys.includes(vendor.vendorKey);
    });
  }, [vendorCatalog, existingSourceIds, selectedSourceIds, otherExcludedVendorKeys]);

  const initialSuggestionVendors = useMemo(
    () => eligibleVendors.slice(0, INITIAL_SUGGESTIONS_COUNT),
    [eligibleVendors],
  );

  const filteredVendors = useMemo(() => {
    if (searchQuery.length < 1) {
      return [];
    }
    const lowerQuery = searchQuery.toLowerCase();
    return eligibleVendors
      .filter((vendor) => vendor.name.toLowerCase().includes(lowerQuery))
      .slice(0, INITIAL_SUGGESTIONS_COUNT);
  }, [eligibleVendors, searchQuery]);

  const autocompleteOptions = useMemo(() => {
    const vendors = searchQuery.length >= 1 ? filteredVendors : initialSuggestionVendors;
    return [...vendors, CUSTOM_SOURCE_OPTION];
  }, [searchQuery, filteredVendors, initialSuggestionVendors]);

  const handleSelectOption = (option: VendorDto | typeof CUSTOM_SOURCE_OPTION | null) => {
    if (!option) return;

    if (option.id === CUSTOM_SOURCE_OPTION.id) {
      setCurrentView("custom-source-details");
      return;
    }

    if (selectedSourceIds.has(option.id)) {
      return;
    }

    const vendor = option as VendorDto;
    setSelectedSources((prev) => [
      ...prev,
      {
        id: vendor.id,
        name: vendor.name,
        vendorKey: vendor.vendorKey ?? undefined,
        logoUrl: vendor.logoUrl,
      },
    ]);
    setSearchQuery("");
  };

  const handleAddCustomSource = (fields: CustomSourceFields) => {
    setSelectedSources((prev) => [
      ...prev,
      {
        name: fields.name,
        custom: fields,
      },
    ]);
    setSearchQuery("");
    setCurrentView("main");
  };

  const handleRemoveSource = (idx: number) => {
    setSelectedSources((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleDone = async () => {
    setIsLoading(true);
    try {
      await onDone(selectedSources);
    } finally {
      setIsLoading(false);
    }
  };

  const isNameAlreadySelected = useCallback(
    (name: string) => {
      const normalized = name.trim().toLowerCase();
      if (!normalized) return false;
      if (selectedSourceNamesLower.has(normalized)) return true;
      if (existingSourceNames?.has(normalized)) return true;
      return false;
    },
    [selectedSourceNamesLower, existingSourceNames],
  );

  if (currentView === "custom-source-details") {
    return (
      <CustomSourceDetailsModal
        initialSourceName={searchQuery}
        categoryOptions={categoryOptions}
        organizationId={organizationId}
        isNameAlreadySelected={isNameAlreadySelected}
        parentTitle={title}
        onBack={() => setCurrentView("main")}
        onCancel={onClose}
        onAdd={handleAddCustomSource}
      />
    );
  }

  return (
    <Modal
      title={title}
      alignText="left"
      alignButton="right"
      firstButton="Cancel"
      firstButtonDisabled={isLoading}
      onFirstButtonClick={onClose}
      secondButton="Done Adding"
      secondButtonColor="primary"
      secondButtonDisabled={isLoading || selectedSources.length === 0}
      onSecondButtonClick={handleDone}
      showOverlay
      closeButton
      onClose={isLoading ? undefined : onClose}
      maxPanelWidth="max-w-[600px]"
      contentContainerClasses="w-full"
    >
      <div className="relative">
        {isLoading && (
          <div className="absolute inset-0 bg-white bg-opacity-50 z-10 flex items-center justify-center rounded-md">
            <CircularProgress size="3em" />
          </div>
        )}

        <div className="space-y-4">
          <p className="text-sm text-gray-600">{description}</p>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Source Name</label>
            <SourceAutocomplete
              options={autocompleteOptions}
              selectedSourceIds={selectedSourceIds}
              query={searchQuery}
              onQueryChange={setSearchQuery}
              onSelect={handleSelectOption}
              placeholder={searchPlaceholder}
              initialSuggestionsCount={INITIAL_SUGGESTIONS_COUNT}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Source(s) Added</label>
            {selectedSources.length === 0 ? (
              <div
                className={clsx(
                  "flex items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 px-6 py-10",
                  "min-h-[80px]",
                )}
              >
                <p className="text-sm text-gray-500">
                  No sources added yet. Search above to add your first one.
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {selectedSources.map((source, idx) => (
                  <SelectedSourceRow
                    key={source.id || `${source.name}-${idx}`}
                    source={source}
                    removeDisabled={isLoading}
                    onRemove={() => handleRemoveSource(idx)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}

interface CustomSourceDetailsModalProps {
  initialSourceName: string;
  categoryOptions: ComboFieldItem[];
  organizationId: OrganizationPublicId;
  isNameAlreadySelected: (name: string) => boolean;
  parentTitle: string;
  onBack: () => void;
  onCancel: () => void;
  onAdd: (fields: CustomSourceFields) => void;
}

function CustomSourceDetailsModal({
  initialSourceName,
  categoryOptions,
  organizationId,
  isNameAlreadySelected,
  parentTitle,
  onBack,
  onCancel,
  onAdd,
}: CustomSourceDetailsModalProps) {
  const [name, setName] = useState(initialSourceName);
  const [category, setCategory] = useState<ComboFieldItem | null>(null);
  const [nameWarning, setNameWarning] = useState<string | null>(null);

  const validateNameAsync = useMemo(
    () =>
      debounce(async (value: string) => {
        const trimmed = value.trim();
        if (!trimmed) {
          setNameWarning(null);
          return;
        }
        if (isNameAlreadySelected(trimmed)) {
          setNameWarning("You've already added this source");
          return;
        }

        const existing = await Api.organizationVendor.searchVendors(organizationId, trimmed);
        const exactMatch = existing.some((v) => v.name.toLowerCase() === trimmed.toLowerCase());
        if (exactMatch) {
          setNameWarning("This source already exists — try searching for it instead");
          return;
        }
        setNameWarning(null);
      }, 500),
    [organizationId, isNameAlreadySelected],
  );

  useEffect(() => {
    validateNameAsync(name);
    return () => validateNameAsync.cancel();
  }, [name, validateNameAsync]);

  const trimmedName = name.trim();
  const trimmedCategory = category?.name.trim() ?? "";
  const canSubmit =
    trimmedName.length > 0 &&
    trimmedCategory.length > 0 &&
    !nameWarning &&
    !isNameAlreadySelected(trimmedName);

  const handleSubmit = () => {
    if (!canSubmit) return;
    onAdd({
      name: trimmedName,
      category: trimmedCategory,
    });
  };

  return (
    <Modal
      showOverlay
      closeButton
      onClose={onCancel}
      maxPanelWidth="max-w-[600px]"
      contentContainerClasses="w-full"
      alignText="left"
      alignButton="right"
      secondButton="Add Custom Source"
      secondButtonColor="primary"
      secondButtonDisabled={!canSubmit}
      onSecondButtonClick={handleSubmit}
      title={
        <div>
          <button
            onClick={onBack}
            className="flex items-center gap-1 text-sm mb-4 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            <span>Back to {parentTitle}</span>
          </button>
          <span>Add Custom Source</span>
        </div>
      }
    >
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
          <input
            type="text"
            className="block w-full rounded-md border border-gray-300 py-2 px-3 text-sm placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={250}
            autoFocus
          />
          {nameWarning && <p className="mt-1 text-sm text-amber-600">{nameWarning}</p>}
        </div>

        <ComboField
          items={categoryOptions}
          value={category}
          onChange={setCategory}
          label="Category"
          placeholder="Type your own or choose from our list"
          showOptionsOnFocus
        />
      </div>
    </Modal>
  );
}

type SourceAutocompleteOption = VendorDto | typeof CUSTOM_SOURCE_OPTION;

interface SourceAutocompleteProps {
  options: SourceAutocompleteOption[];
  selectedSourceIds: Set<string>;
  query: string;
  onQueryChange: (query: string) => void;
  onSelect: (option: SourceAutocompleteOption | null) => void;
  placeholder: string;
  initialSuggestionsCount: number;
}

function SourceAutocomplete({
  options,
  selectedSourceIds,
  query,
  onQueryChange,
  onSelect,
  placeholder,
  initialSuggestionsCount,
}: SourceAutocompleteProps) {
  const [isFocused, setIsFocused] = useState(false);

  const vendorOptions = options.filter(
    (option): option is VendorDto => option.id !== CUSTOM_SOURCE_OPTION.id,
  );
  const showCustomOption = options.some((o) => o.id === CUSTOM_SOURCE_OPTION.id);

  // Plain div for initial suggestions avoids Headless UI auto-selecting the first option on blur.
  const showInitialSuggestions =
    initialSuggestionsCount > 0 &&
    isFocused &&
    query === "" &&
    (vendorOptions.length > 0 || showCustomOption);

  const showFilteredOptions =
    !showInitialSuggestions && query.length >= 1 && (vendorOptions.length > 0 || showCustomOption);

  return (
    <Combobox onChange={onSelect} value={null}>
      <div className="relative">
        <div className="relative">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
          </div>
          <Combobox.Input
            className="block w-full rounded-md border border-gray-300 py-2 pl-10 pr-3 text-sm placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            placeholder={placeholder}
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            autoComplete="off"
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                setIsFocused(false);
              }
            }}
          />
        </div>

        {showInitialSuggestions && (
          <div
            onMouseDown={(e) => e.preventDefault()}
            className="absolute z-10 mt-1 max-h-[264px] w-full overflow-y-auto p-3 bg-white rounded-md"
            style={{
              scrollbarWidth: "none",
              boxShadow: "0px 2px 12px 0px rgba(0, 0, 0, 0.25)",
            }}
          >
            {vendorOptions.map((vendor) => (
              <SourceOptionRow
                key={vendor.id}
                option={vendor}
                isAdded={selectedSourceIds.has(vendor.id)}
                onSelect={() => {
                  setIsFocused(false);
                  onSelect(vendor);
                }}
              />
            ))}
            {showCustomOption && (
              <SourceOptionRow
                option={CUSTOM_SOURCE_OPTION}
                isAdded={false}
                onSelect={() => {
                  setIsFocused(false);
                  onSelect(CUSTOM_SOURCE_OPTION);
                }}
              />
            )}
          </div>
        )}

        {showFilteredOptions && (
          <Combobox.Options
            className="absolute z-10 mt-1 max-h-[264px] w-full overflow-y-auto p-3 bg-white rounded-md"
            style={{
              scrollbarWidth: "none",
              boxShadow: "0px 2px 12px 0px rgba(0, 0, 0, 0.25)",
            }}
          >
            {vendorOptions.map((vendor) => {
              const isAdded = selectedSourceIds.has(vendor.id);
              return (
                <Combobox.Option key={vendor.id} value={vendor} disabled={isAdded} as="div">
                  {({ active }) => (
                    <SourceOptionRow option={vendor} isAdded={isAdded} active={active} />
                  )}
                </Combobox.Option>
              );
            })}

            {showCustomOption && (
              <Combobox.Option value={CUSTOM_SOURCE_OPTION} as="div">
                {({ active }) => (
                  <SourceOptionRow option={CUSTOM_SOURCE_OPTION} isAdded={false} active={active} />
                )}
              </Combobox.Option>
            )}
          </Combobox.Options>
        )}
      </div>
    </Combobox>
  );
}

function SourceOptionRow({
  option,
  isAdded,
  active = false,
  onSelect,
}: {
  option: SourceAutocompleteOption;
  isAdded: boolean;
  active?: boolean;
  onSelect?: () => void;
}) {
  const isCustomOption = option.id === CUSTOM_SOURCE_OPTION.id;

  const row = (
    <div
      className={clsx(
        "flex items-center gap-2 mb-1 px-4 py-4 border border-solid rounded cursor-pointer",
        active && !isAdded ? "border-neutral-900" : "border-neutral-200",
        isAdded ? "opacity-50 cursor-not-allowed" : "",
        isCustomOption && active ? "border-primary-500" : "",
      )}
      onClick={onSelect}
    >
      {isCustomOption ? (
        <div className="h-6 w-6 rounded bg-gray-200 flex items-center justify-center flex-shrink-0">
          <Squares2X2Icon className="h-4 w-4 text-gray-500" />
        </div>
      ) : option.logoUrl ? (
        <img src={option.logoUrl} alt={option.name} className="h-6 w-6 flex-shrink-0" />
      ) : (
        <div className="h-6 w-6 rounded bg-gray-200 flex items-center justify-center flex-shrink-0">
          <span className="text-xs text-gray-500">{option.name.charAt(0).toUpperCase()}</span>
        </div>
      )}
      <span className={clsx("text-sm text-gray-900", active && !isAdded ? "font-medium" : "")}>
        {isCustomOption ? `${option.name}…` : option.name}
      </span>
      {isAdded && (
        <span className="ml-auto text-xs font-medium text-green-600 bg-green-100 px-2 py-0.5 rounded">
          Added
        </span>
      )}
      {!isAdded && isCustomOption && (
        <span className="ml-auto">
          <Button color="tertiary" size="s" onClick={() => {}}>
            Add
          </Button>
        </span>
      )}
    </div>
  );

  return row;
}

interface SelectedSourceRowProps {
  source: SelectedSource;
  removeDisabled?: boolean;
  onRemove: () => void;
}

function SelectedSourceRow({ source, removeDisabled, onRemove }: SelectedSourceRowProps) {
  const isCustom = Boolean(source.custom);

  return (
    <div className="flex items-center justify-between py-2 px-3 border border-gray-200 rounded-md">
      <div className="flex items-center gap-3 min-w-0">
        {source.logoUrl ? (
          <img src={source.logoUrl} alt={source.name} className="h-6 w-6 flex-shrink-0" />
        ) : (
          <div className="h-6 w-6 rounded bg-gray-200 flex items-center justify-center flex-shrink-0">
            {isCustom ? (
              <Squares2X2Icon className="h-4 w-4 text-gray-500" />
            ) : (
              <span className="text-xs text-gray-500">{source.name.charAt(0).toUpperCase()}</span>
            )}
          </div>
        )}
        <div className="min-w-0">
          <span className="text-sm font-medium text-gray-900">{source.name}</span>
          {source.custom?.category && (
            <div className="text-xs text-gray-500 truncate">{source.custom.category}</div>
          )}
        </div>
      </div>
      <Button color="tertiary" size="s" disabled={removeDisabled} onClick={onRemove}>
        Remove
      </Button>
    </div>
  );
}
